import { useEffect, useMemo } from 'react';

import { useCategoryTreeItem } from '../categoryTree';
import { tvpil } from '../compat/tvpil';
import { useFavoriteChannelsDictionary } from '../favorite/useFavoriteChannelsDictionary';
import { useLastWatched } from '../lastWatched';
import { useMostWatched } from '../mostWatched';

import { composeChannels } from './composeChannels';
import { useChannelAssets } from './useChannelAssets';
import { useChannelCollection } from './useChannelCollection';
import { useEntitledChannels } from './useEntitledChannels';
import { getLimit } from './utils';

type UseChannelsProps = {
  pageReferenceId?: string;
  referenceId: string;
};

export function useChannels({ referenceId, pageReferenceId }: UseChannelsProps) {
  const category = useCategoryTreeItem(referenceId);

  const { channelAssetsMap } = useChannelAssets();
  const { entitledChannels } = useEntitledChannels();
  const { channelCollection, isLoading, error, refetch } = useChannelCollection({ referenceId });
  const { favoriteChannels } = useFavoriteChannelsDictionary();
  const { lastWatched, isLoading: isLoadingLastWatched } = useLastWatched({
    pageReferenceId,
    referenceId,
    enabled: Boolean(pageReferenceId),
  });
  const { mostWatched, isLoading: isLoadingMostWatched } = useMostWatched({ referenceId });

  useEffect(() => {
    // This is needed in order to tell lastWatchedLinearStore to store
    // a last watched linear against correct page reference so it would
    // work as expected on genre pages
    if (pageReferenceId) {
      tvpil.lastWatchedLinearStore.setCurrentHeroRail(pageReferenceId);
    } else {
      tvpil.lastWatchedLinearStore.clearCurrentHeroRail();
    }
  });

  const limit = category ? getLimit(category) : undefined;

  const orderBatches = category?.epgProperty?.orderBatches;

  const channels = useMemo(() => {
    if (isLoadingLastWatched || isLoadingMostWatched) {
      return;
    }

    if (!channelAssetsMap || !entitledChannels || !channelCollection || !favoriteChannels) {
      return;
    }

    return composeChannels({
      channelAssetsMap,
      channelCollection,
      entitledChannels,
      favoriteChannels,
      lastWatched,
      limit,
      mostWatched,
      orderBatches,
    });
  }, [
    channelAssetsMap,
    channelCollection,
    entitledChannels,
    favoriteChannels,
    isLoadingLastWatched,
    isLoadingMostWatched,
    lastWatched,
    limit,
    mostWatched,
    orderBatches,
  ]);

  return { channels, isLoading: isLoading || isLoadingLastWatched || isLoadingMostWatched, error, refetch };
}
